export type UserRole =
  | "admin"
  | "operator"
  | "controller"
  | "marketplace_manager"
  | "scan_operator";

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: "Администратор",
  operator: "Оператор упаковки",
  controller: "Контролёр / склад",
  marketplace_manager: "Менеджер маркетплейса",
  scan_operator: "Сканировка",
};

export type Marketplace = "wb" | "ozon" | "yandex" | "other";

export interface User {
  id: number;
  login: string;
  name: string;
  role: UserRole;
  is_active: boolean;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export type CodeBatchStatus = "uploaded" | "active" | "archived";

export interface Batch {
  id: number;
  source_filename: string;
  ozon_client_id: number | null;
  status: CodeBatchStatus;
  codes_total: number;
  codes_free: number;
  labels_not_printed: number;
  label_pdf_path: string | null;
  label_pdf_pages: number | null;
  label_pdf_matched: number | null;
  uploaded_by: number | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface AttachedPdfFile {
  filename: string;
  pages: number;
  matched: number;
  unmatched_pages: number[];
}

export interface AttachPdfResponse {
  batch_id: number;
  files: AttachedPdfFile[];
  total_pages: number;
  total_matched: number;
}

export interface LabelPdfFile {
  filename: string;
  exists: boolean;
  size: number;
}

export interface ImportErrorOut {
  line_number: number;
  reason: string;
}

export interface BatchImportResponse {
  batch: Batch;
  imported: number;
  duplicates: number;
  errors: ImportErrorOut[];
}

export type ShipmentStatus =
  | "draft"
  | "scanning"
  | "picked"
  | "booked"
  | "exported"
  | "in_turnover"
  | "closed"
  | "cancelled";

/**
 * Пользователь видит на экране только эти подписи, а не технические ключи.
 * Сам ключ остаётся в БД (enum) и в API — он стабильный идентификатор.
 */
export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  draft: "Новая",
  scanning: "Сканируется",
  picked: "Собрана, нужен слот",
  booked: "Забронирована",
  exported: "CSV выгружен",
  in_turnover: "Введена в оборот",
  closed: "Отгружена",
  cancelled: "Отменена",
};

export interface Shipment {
  id: number;
  number: string;
  name: string | null;
  status: ShipmentStatus;
  // Кабинет Ozon (юрлицо) отгрузки. null у исторических отгрузок (до разбивки
  // по кабинетам) — для них сличение идёт по всем кабинетам, как раньше.
  ozon_client_id: number | null;
  warehouse_id: string | null;
  responsible_user_id: number | null;
  closed_at: string | null;
  comment: string | null;
  marketplace: Marketplace | null;
  delivery_number: string | null;
  slot_at: string | null;
  booked_at: string | null;
  picked_at: string | null;
  in_turnover_at: string | null;
  marking_zip_filename: string | null;
  marking_zip_uploaded_at: string | null;
  boxes_planned: number | null;
  active_box_number?: number | null;
  boxes_actual: number | null;
  pallets_actual: number | null;
  cluster: number | null;
  // Режим наборов:
  //   null  — оператор ещё не ответил «это наборы?» (UI открывает модалку);
  //   false — «нет, одиночные товары»;
  //   true  — «да, наборы», в отгрузке появляются партии (kit_batches),
  //           каждая со своим kit_size.
  // Поля ниже (kit_size, kit_size_locked_at) — зеркало АКТИВНОЙ (последней)
  // партии для обратной совместимости старого UI.
  is_kit_order: boolean | null;
  kit_size: number | null;
  kit_size_locked_at: string | null;
  // Полный список партий, отсортирован по batch_no. Пустой для не-kit.
  kit_batches: KitBatch[];
  created_at: string;
  updated_at: string;
}

export interface KitBatch {
  id: number;
  batch_no: number;
  kit_size: number | null;        // null = партия в обучении
  locked_at: string | null;
  items_count: number;            // КИЗов в партии
  kits_count: number | null;      // завершённых наборов (items // kit_size)
}

export interface ShipmentBox {
  box_number: number;
  printed_at: string | null;
  printed_by_user_id: number | null;
  print_count: number;
}

export interface ShipmentListItem extends Shipment {
  items_count: number;
  // Число завершённых наборов (items_count // kit_size). null если
  // is_kit_order=false или kit_size не зафиксирован.
  kit_count: number | null;
}

export type CodeStatus =
  | "free"
  | "reserved"
  | "selected"
  | "exporting"
  | "used"
  | "written_off"
  | "error";

export type LabelStatus =
  | "not_printed"
  | "printed"
  | "applied"
  | "damaged"
  | "lost"
  | "written_off"
  | "used";

export interface Code {
  id: number;
  batch_id: number;
  gtin: string;
  serial: string;
  composite_key: string;
  code_status: CodeStatus;
  label_status: LabelStatus;
  shipment_id: number | null;
  reserved_for_entity_type: string | null;
  reserved_for_entity_id: string | null;
  reserved_at: string | null;
  used_at: string | null;
  written_off_at: string | null;
  comment: string | null;
}

export interface ApiError {
  error?: string;
  detail?: string;
  missing_gtins?: string[];
}

export interface OzonCabinet {
  ozon_client_id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
}

export interface OzonProduct {
  ozon_client_id: number;
  article: string;
  barcode: string | null;
  ozon_product_id: string | null;
  sku: string | null;
  name: string | null;
  brand: string | null;
  status: string | null;
  last_seen_at: string;
}

export interface OzonImportResponse {
  inserted: number;
  updated: number;
  source_file_path: string;
}

export interface OzonMatchRow {
  article: string;
  name: string | null;
  qty: number;            // Физических КИЗ (всегда)
  qty_kits: number | null; // Наборов (qty // kit_size); null если не kit-отгрузка
}

export interface OzonMatchResponse {
  matched: OzonMatchRow[];
  missing_gtins: string[];
  is_kit_order: boolean;
  kit_size: number | null;
}

export interface OzonFreeStockRow {
  article: string | null;
  name: string | null;
  barcode: string;
  free_count: number;
  printable_count: number;
}

export interface OzonAccountRefresh {
  client_id: number;
  inserted: number;
  updated: number;
  error: string | null;
}

export interface OzonRefreshResponse {
  total_inserted: number;
  total_updated: number;
  per_account: OzonAccountRefresh[];
}
